import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import { FormCheck } from '../form';
import { FilterRow } from './filter';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { ChevronDownIcon } from 'lucide-react';

const FilterSelectMultiBadges = React.forwardRef(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('relative flex h-5 items-center gap-1 overflow-hidden', className)}
    {...props}
  />
));
FilterSelectMultiBadges.displayName = 'FilterSelectMultiBadges';

const FilterSelectMultiContent = React.forwardRef(({ className, isOpen, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      // styles
      'absolute left-0 right-0 top-[calc(100%+4px)] z-10 rounded border border-solid border-neutral bg-white py-2.5 shadow-dropshadow',
      'transition-all duration-150 ease-in-out',
      isOpen ? 'pointer-events-auto visible translate-y-0 opacity-100' : 'pointer-events-none invisible -translate-y-3 opacity-0',
      className
    )}
    {...props}
  />
));
FilterSelectMultiContent.displayName = 'FilterSelectMultiContent';

export function FilterSelectMulti({ data, count, label, placeholder, ...props }) {
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = React.useState(false);

  const menuRef = React.useRef();

  function handleOpen() {
    setIsOpen(!isOpen);
  }

  React.useEffect(() => {
    if (!isOpen) return;

    const closeOnEscapeKeyDown = event => event.key === 'Escape' && isOpen && setIsOpen(false);
    const closeOnClickOutside = event => !menuRef.current.contains(event.target) && setIsOpen(false);

    document.body.addEventListener('keydown', closeOnEscapeKeyDown);
    document.addEventListener('mouseup', closeOnClickOutside);

    return () => {
      document.body.removeEventListener('keydown', closeOnEscapeKeyDown);
      document.removeEventListener('mouseup', closeOnClickOutside);
    };
  }, [isOpen]);

  const checked = data.filter(check => check.value === true);

  const placeholderText = placeholder ? placeholder : t('filter.blankLabel');

  const id = React.useId();

  return (
    <FilterRow {...props}>
      {label && <Label htmlFor={id}>{label}</Label>}

      <div
        className="relative"
        ref={menuRef}
      >
        <Button
          id={id}
          variant="outline"
          type="button"
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={`${id}-menu`}
          className={cn(
            'w-full justify-between rounded-md border-primary-lightest bg-background px-3 text-sm font-normal text-neutral outline-none outline-offset-0 hover:text-neutral-dark',
            // Hover and focus styles
            'hover:border-neutral-light hover:bg-bright hover:text-neutral-dark focus-visible:outline-[3px]'
          )}
          onClick={handleOpen}
        >
          {!count && (
            <FilterSelectMultiBadges>
              {checked.length === 0 && placeholderText}
              {checked.length > 0 &&
                checked.map(check => (
                  <Badge
                    variant="primary-light"
                    size="xs"
                    className="shrink-0"
                    title={check.label}
                    key={check.label}
                  >
                    {check.label}
                  </Badge>
                ))}
            </FilterSelectMultiBadges>
          )}

          {count && (
            <FilterSelectMultiBadges>
              {checked.length} {checked.length > 1 ? 'selecionados' : 'selecionado'}
            </FilterSelectMultiBadges>
          )}

          <ChevronDownIcon
            size={16}
            className="shrink-0 text-muted-foreground/80"
            aria-hidden="true"
          />
        </Button>

        <FilterSelectMultiContent
          id={`${id}-menu`}
          isOpen={isOpen}
        >
          {data?.map(check => (
            <FormCheck
              key={check.label}
              label={check.label}
              checked={check.value}
              onCheckedChange={check.onChange}
              className={cn('p-3 transition duration-150 hover:bg-bright', check.value && 'bg-bright')}
            />
          ))}
        </FilterSelectMultiContent>
      </div>
    </FilterRow>
  );
}
